import React from 'react';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import useWithdrawalCoinOptions from '../../hooks/useWithdrawalCoinOptions';

const COINS_WITH_DESTINATION_TAG = ['XRP'];

/**
 * Shared withdrawal fields: USD amount, coin picker (from coin_rate_changes), estimate, address.
 */
export default function WithdrawalFormFields({
  amount,
  setAmount,
  coinName,
  setCoinName,
  address,
  setAddress,
  destinationTag = '',
  setDestinationTag,
  error = {},
}) {
  const {
    coinOptions,
    estimateCoinDeduction,
    formatUsd,
    formatCryptoAmount,
    getCoinUnits,
    getCoinUsd,
    coinRates,
  } = useWithdrawalCoinOptions();

  const usdAmount = parseFloat(amount) || 0;
  const { coinAmount: coinToDeduct, priceUsd } = estimateCoinDeduction(coinName, usdAmount);
  const availableUnits = coinName ? getCoinUnits(coinName) : 0;
  const availableUsd = coinName ? getCoinUsd(coinName) : 0;

  return (
    <>
      <FormControl fullWidth sx={{ marginBottom: 2 }}>
        <InputLabel htmlFor="amount">Amount (USD)</InputLabel>
        <OutlinedInput
          onChange={(e) => setAmount(e.target.value)}
          id="amount"
          type="number"
          name="amount"
          label="Amount (USD)"
          value={amount}
          error={Boolean(error?.amount)}
        />
        {error?.amount && (
          <Typography color="error" variant="caption">{error.amount}</Typography>
        )}
      </FormControl>

      {coinRates.length > 0 && (
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel id="withdraw-coin-label">Select payment method (coin)</InputLabel>
          <Select
            labelId="withdraw-coin-label"
            onChange={(e) => setCoinName(e.target.value)}
            id="coinName"
            name="coinName"
            label="Select payment method (coin)"
            value={coinName}
            error={Boolean(error?.coin_name)}
          >
            <MenuItem value="">Select coin</MenuItem>
            {coinOptions.map((coin) => (
              <MenuItem
                key={coin.symbol}
                value={coin.symbol}
                disabled={coin.units <= 0}
              >
                {coin.symbol}
                {coin.units > 0
                  ? ` (${formatCryptoAmount(coin.units)} · ${formatUsd(coin.usdBalance)})`
                  : ' (0 balance)'}
              </MenuItem>
            ))}
          </Select>
          {coinName && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Available: {formatCryptoAmount(availableUnits)} {coinName} (≈ {formatUsd(availableUsd)})
            </Typography>
          )}
        </FormControl>
      )}

      {coinName && usdAmount > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 193, 7, 0.08)',
            border: '1px solid rgba(255, 193, 7, 0.25)',
          }}
        >
          {priceUsd > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Coin amount to deduct
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCryptoAmount(coinToDeduct)} {coinName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {formatUsd(usdAmount)} @ {formatUsd(priceUsd)} / {coinName}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="warning.main">
              Price unavailable for {coinName}.
            </Typography>
          )}
        </Box>
      )}

      <FormControl fullWidth sx={{ marginBottom: 2 }}>
        <InputLabel htmlFor="address">Wallet address</InputLabel>
        <OutlinedInput
          onChange={(e) => setAddress(e.target.value)}
          id="address"
          type="text"
          name="address"
          label="Wallet address"
          value={address}
          error={Boolean(error?.address)}
        />
        {error?.address && (
          <Typography color="error" variant="caption">{error.address}</Typography>
        )}
      </FormControl>

      {COINS_WITH_DESTINATION_TAG.includes(coinName) && setDestinationTag && (
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel htmlFor="destinationTag">Destination tag</InputLabel>
          <OutlinedInput
            onChange={(e) => setDestinationTag(e.target.value.replace(/\D/g, ''))}
            id="destinationTag"
            type="text"
            inputMode="numeric"
            name="destinationTag"
            label="Destination tag"
            value={destinationTag}
            error={Boolean(error?.destination_tag)}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Required by most XRP exchanges and custodial wallets. Leave blank only if your wallet does not use a tag.
          </Typography>
          {error?.destination_tag && (
            <Typography color="error" variant="caption">{error.destination_tag}</Typography>
          )}
        </FormControl>
      )}
    </>
  );
}

export { useWithdrawalCoinOptions };
